import _ from 'lodash/fp'
import gql from 'graphql-tag'
import { all, call, put, take, takeLatest } from 'redux-saga/effects'

import * as usersReducer from '../reducers/users'
import * as authReducer from '../reducers/auth'
import * as snack from './snackMessage'

import { getCurrentClubId, deleteEffect, prependClubsUrl } from './network'

import { apolloClient } from '@fe/business/src/apollo'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

const CLUB_USERS_QUERY = gql`
  query ClubUsers($clubId: ID!) {
    clubUsers(clubId: $clubId) {
      id
      admin
      clubId
      firstName
      lastName
      email
      photoFileName
      lastSignInAt
      roles
    }
  }
`

const SET_USER_ROLES_MUTATION = gql`
  mutation SetUserRoles($input: SetUserRolesInput!) {
    UsersMutation {
      setUserRoles(input: $input) {
        inserts
        deletes
        primaryClubUpdated
      }
    }
  }
`

const CREATE_CLUB_USER_MUTATION = gql`
  mutation CreateClubUser($input: CreateClubUserInput!) {
    UsersMutation {
      createClubUser(input: $input) {
        id
        admin
        clubId
        firstName
        lastName
        email
        photoFileName
        lastSignInAt
        roles
      }
    }
  }
`

const UPDATE_OWN_PROFILE_MUTATION = gql`
  mutation UpdateOwnProfile($input: UpdateOwnProfileInput!) {
    UsersMutation {
      updateOwnProfile(input: $input) {
        id
        admin
        clubId
        firstName
        lastName
        email
        photoFileName
        lastSignInAt
        roles
      }
    }
  }
`

function * getUsersEffect (payload) {
  try {
    const clubId = _.get('clubId', payload) || (yield call(getCurrentClubId))
    const response = yield call([apolloClient, apolloClient.query], {
      query: CLUB_USERS_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.clubUsers', response)
  } catch (error) {
    return { error }
  }
}

function * createUserEffect (user) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: CREATE_CLUB_USER_MUTATION,
      variables: {
        input: {
          clubId: user.club_id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          roles: user.roles
        }
      }
    })
    return _.get('data.UsersMutation.createClubUser', response)
  } catch (error) {
    return { error }
  }
}

function * updateUserEffect (payload) {
  const profile = _.get('profile', payload)
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPDATE_OWN_PROFILE_MUTATION,
      variables: {
        input: {
          firstName: _.get('firstName', profile),
          lastName: _.get('lastName', profile),
          email: _.get('email', profile),
          language: _.get('language', profile)
        }
      }
    })
    return _.get('data.UsersMutation.updateOwnProfile', response)
  } catch (error) {
    return { error }
  }
}

function * deleteUserEffect (userId) {
  const request = {
    url: (yield call(prependClubsUrl, `users/${userId}`))
  }
  const chan = yield call(deleteEffect, request)
  return yield take(chan)
}

function * editUserRolesEffect (user) {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: SET_USER_ROLES_MUTATION,
      variables: {
        input: {
          clubId,
          userId: user.id,
          roles: user.roles
        }
      }
    })
    return _.get('data.UsersMutation.setUserRoles', response)
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

function * getUsersFlow ({ payload }) {
  const users = yield call(getUsersEffect, payload)
  if (users.error) {
    yield put(usersReducer.actions.getUsersFailure())
  } else {
    yield put(usersReducer.actions.getUsersSuccess())
  }
  yield put(usersReducer.actions.getUsersDone(_.keyBy('id', users)))
}

function * createUserFlow ({ payload }) {
  const result = yield call(createUserEffect, payload)
  if (result.error) {
    yield put(usersReducer.actions.createUserFailure())
  } else {
    yield put(usersReducer.actions.createUserSuccess())
  }
  yield put(usersReducer.actions.createUserDone())
  yield put(usersReducer.actions.getUsers())
}

function * deleteUserFlow ({ payload }) {
  const result = yield call(deleteUserEffect, payload)
  if (result.error) {
    yield put(usersReducer.actions.deleteUserFailure())
  } else {
    yield put(usersReducer.actions.deleteUserSuccess())
  }
  yield put(usersReducer.actions.deleteUserDone())
  yield put(usersReducer.actions.getUsers())
}

function * editUserFlow ({ payload }) {
  const result = yield call(editUserRolesEffect, payload)
  if (result.error) {
    const errorCode = _.get('error.graphQLErrors[0].extensions.code', result)
    if (errorCode === 'FORBIDDEN') {
      yield snack.setMessage(`You can't change this user's roles`)
    } else {
      yield snack.genericError()
    }
    yield put(usersReducer.actions.editUserFailure(result.error))
  } else {
    yield put(usersReducer.actions.getUsers())
    yield take(usersReducer.types.GET_USERS_DONE)
    yield put(usersReducer.actions.editUserSuccess(result))
  }
}

function * updateUserFlow ({ payload }) {
  const result = yield call(updateUserEffect, payload)
  if (result.error) {
    return
  }
  yield put(authReducer.actions.reloadProfileRequest())
}

export default function * () {
  return yield all([
    takeLatest(usersReducer.types.GET_USERS, getUsersFlow),
    takeLatest(usersReducer.types.CREATE_USER, createUserFlow),
    takeLatest(usersReducer.types.DELETE_USER, deleteUserFlow),
    takeLatest(usersReducer.types.EDIT_USER_REQUEST, editUserFlow),
    takeLatest(usersReducer.types.UPDATE_USER, updateUserFlow)
  ])
}
